import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Card, Container, Spinner, Badge, Row, Col, Alert } from 'react-bootstrap';
import { FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import moment from 'moment';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import Calendar from '../../components/Calandar';
import { stringToColor, getInitials } from '../../utility';
import api from '../../services/api';


const UserDashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});
  const [nextAppraisalDate, setNextAppraisalDate] = useState(null);
  // Set default year to 2025 to match the test data
  const [currentYear, setCurrentYear] = useState(2025);

  // Format attendance data for the calendar
  const formatAttendanceData = useCallback((attendance) => {
    const formattedData = {};
    
    console.log('Received attendance records:', attendance);
    
    attendance.forEach((record, index) => {
      console.log(`Processing record ${index}:`, record);
      
      if (!record || !record.date) {
        console.warn('Skipping invalid record:', record);
        return;
      }
      
      const date = moment(record.date);
      if (!date.isValid()) {
        console.warn('Invalid date in record:', record);
        return;
      }
      
      // Ensure month is in title case (e.g., 'Oct' instead of 'oct')
      const month = date.format('MMM');
      const formattedMonth = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
      const day = date.date();
      const status = (record.status || '').toLowerCase();
      
      console.log(`Processing: ${formattedMonth} ${day}, Status: ${status}`);
      
      // Initialize month if not exists
      if (!formattedData[formattedMonth]) {
        formattedData[formattedMonth] = {};
      }
      
      // Ensure day is stored as a string for consistent access
      const dayStr = day.toString();
      
      // Only include 'present' status, all other statuses are treated as regular days
      // unless they have leave information which is handled separately
      if (status === 'present') {
        formattedData[formattedMonth][dayStr] = 'present';
      }
      
      console.log(`Setting ${formattedMonth} ${dayStr} to status:`, status);
      
      // Build tooltip from leaves array if provided, excluding rejected leaves
      const leaves = Array.isArray(record.leaves) 
        ? record.leaves.filter(leave => !leave.leave_status || leave.leave_status.toLowerCase() !== 'rejected')
        : [];
      if (leaves.length > 0) {
        // Ensure the day is marked as leave
        formattedData[formattedMonth][dayStr] = 'leave';

        // Compose tooltip lines like: "Annual Leave (1 day)" or "Sick Leave (Half Day)"
        const tooltip = leaves.map((l) => {
          const type = l?.leave_type || 'Leave';
          const dur = typeof l?.duration === 'number' ? l.duration : (typeof l?.leave_duration === 'number' ? l.leave_duration : null);
          let humanDuration = '';
          if (dur === 0.5) humanDuration = 'Half Day';
          else if (dur === 1) humanDuration = '1 day';
          else if (dur && dur > 1) humanDuration = `${dur} days`;
          else humanDuration = '';
          return humanDuration ? `${type} (${humanDuration})` : type;
        }).join('\n');

        if (!formattedData[formattedMonth].note) {
          formattedData[formattedMonth].note = {};
        }
        formattedData[formattedMonth].note[dayStr] = tooltip;
      } else {
        // Fallback: Add generic note for non-present/absent statuses
        if (status && status !== 'present' && status !== 'absent') {
          if (!formattedData[formattedMonth].note) {
            formattedData[formattedMonth].note = {};
          }
          formattedData[formattedMonth].note[dayStr] = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        }
      }
    });
    
    console.log('Final formatted data:', JSON.stringify(formattedData, null, 2));
    return formattedData;
  }, []);

  const fetchAttendanceData = useCallback(async (year) => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const startDate = moment().year(year).startOf('year').format('YYYY-MM-DD');
      const endDate = moment().year(year).endOf('year').format('YYYY-MM-DD');
      
      console.log(`Fetching attendance for ${year} from ${startDate} to ${endDate}`);
      
      // Fetch attendance data for the year using the new endpoint
      const response = await api.get(`/attendances/by-date-range?start_date=${startDate}&end_date=${endDate}&uid=${user.id}`);
      
      if (response.data && Array.isArray(response.data)) {
        console.log('Raw attendance data:', response.data);
        const formattedData = formatAttendanceData(response.data);
        console.log('Formatted attendance data:', formattedData);
        setAttendanceData(formattedData);
      }
      
      // Get next appraisal date (if available in user data)
      if (user?.next_appraisal_date) {
        setNextAppraisalDate(new Date(user.next_appraisal_date));
        // Fallback to 3 months from now if no appraisal date is set
        setNextAppraisalDate(moment().add(3, 'months').toDate());
      }
      
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError('Failed to load attendance data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user, formatAttendanceData]);

  // Handle year change in the calendar
  const handleYearChange = (newYear) => {
    setCurrentYear(newYear);
    fetchAttendanceData(newYear);
  };

  // Initial data fetch
  useEffect(() => {
    fetchAttendanceData(currentYear);

    // Set next appraisal date: use user value if present, otherwise leave null
    if (user?.next_appraisal_date) {
      setNextAppraisalDate(moment(user.next_appraisal_date).toDate());
    } else {
      setNextAppraisalDate(null);
    }
  }, [fetchAttendanceData, currentYear]);

  // Calculate attendance summary
  const calculateAttendanceSummary = useCallback(() => {
    let present = 0;
    let absent = 0;
    let leave = 0;
    
    Object.values(attendanceData).forEach(monthData => {
      Object.entries(monthData).forEach(([key, value]) => {
        if (key === 'note') return;
        if (value === 'present') present++;
        else if (value === 'absent') absent++;
        else leave++;
      });
    });
    
    return { present, absent, leave };
  }, [attendanceData]);
  
  const attendanceSummary = calculateAttendanceSummary();

  if (loading) {
    return (
      <Layout>
        <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <TopBar title="My Dashboard" />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>My Dashboard</h2>
          </Col>
        </Row>
        
        {error && (
          <Alert variant="danger" className="mb-4">
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}
        
        {/* Top Cards Row: compact cards filling first row */}
        <Row className="mb-4 g-3">
          {/* Profile (compact) */}
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                  style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: stringToColor(user?.name || ''),
                    color: 'white',
                    fontSize: '22px',
                    fontWeight: 'bold'
                  }}
                >
                  {getInitials(user?.name || '')}
                </div>
                <div>
                  <div className="fw-semibold">{user?.name || 'User'}</div>
                  {/* <Badge bg="primary">{user?.employee_id || 'ID: N/A'}</Badge> */}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Next Appraisal */}
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="text-center d-flex flex-column justify-content-center">
                <div className="text-muted small">Next Appraisal</div>
                <div className="fw-bold">{nextAppraisalDate ? moment(nextAppraisalDate).format('MMM DD, YYYY') : 'Not set'}</div>
              </Card.Body>
            </Card>
          </Col>

          {/* Present count */}
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <span className="d-inline-block rounded-circle bg-success me-2" style={{width: '12px', height: '12px'}}></span>
                  <span>Present</span>
                </div>
                <Badge bg="light" text="dark">{attendanceSummary.present}</Badge>
              </Card.Body>
            </Card>
          </Col>

          {/* Leave count */}
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <span className="d-inline-block rounded-circle bg-warning me-2" style={{width: '12px', height: '12px'}}></span>
                  <span>Leave</span>
                </div>
                <Badge bg="light" text="dark">{attendanceSummary.leave}</Badge>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Calendar Section - Full Width */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">
                    <FaCalendarAlt className="me-2" />
                    My Attendance - {currentYear}
                  </h5>
                  <div className="d-flex">
                    <div className="d-flex align-items-center me-3">
                      <span className="d-inline-block rounded-circle bg-success me-1" style={{width: '12px', height: '12px'}}></span>
                      <small>Present</small>
                    </div>
                    <div className="d-flex align-items-center me-3">
                      <span className="d-inline-block rounded-circle bg-danger me-1" style={{width: '12px', height: '12px'}}></span>
                      <small>Absent</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="d-inline-block rounded-circle bg-warning me-1" style={{width: '12px', height: '12px'}}></span>
                      <small>Leave</small>
                    </div>
                  </div>
                </div>
                
                {console.log('Calendar props:', { marks: attendanceData, year: currentYear, loading })}
                {console.log('Rendering Calendar with props:', { marks: attendanceData, year: currentYear, loading })}
                <Calendar 
                  marks={attendanceData} 
                  year={currentYear}
                  onYearChange={handleYearChange}
                  loading={loading}
                />
                
                <div className="mt-3 text-muted small">
                  <p className="mb-1"><strong>Note:</strong> Hover over marked dates to see details.</p>
                  <p className="mb-0">Use the arrow buttons to navigate between years.</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default UserDashboard;